import { t } from '@if/core/i18n'
import { Button, Stack } from '@mui/material'
import { apiClient } from '@src/services/http-common'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import { ReactNode, useEffect } from 'react'

interface ConflictHandlerProps {
  children?: ReactNode
}

const ConflictHandler = ({ children }: ConflictHandlerProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 409) {
          // using component trans for supporting language switching
          enqueueSnackbar(t('common.messages.conflict'), {
            variant: 'error',
            persist: true,
            key: 'conflict-error',
            preventDuplicate: true,
            action: (key) => (
              <Stack direction="row" spacing={0.5}>
                <Button color="inherit" onClick={() => closeSnackbar(key)}>
                  {t('common.cancel')}
                </Button>
                <Button color="inherit" onClick={() => {
                  void queryClient.invalidateQueries()
                  closeSnackbar(key)
                }}>
                  {t('common.reload')}
                </Button>
              </Stack>
            ),
          })
        }
        throw error
      },
    )
    return () => {
      apiClient.interceptors.response.eject(interceptorId)
    }
  }, [queryClient, enqueueSnackbar, closeSnackbar])

  return children
}

export default ConflictHandler
